import { ConfigurationManagerErrors, GpsrActions, GpsrGetters, GpsrMutations, GpsrState, InitialDataHash } from "@medapro-types/Gpsr/gpsr-store";
import { EconomicOperatorDefaultAttributes } from "@medapro-types/common/compliance/economic-operator-enums";
import { EconomicOperator, EconomicOperatorAttribute } from "@medapro-types/common/compliance/economic-operators";
import eventLogger from "@services/ApiLogger/EventLogger.js";
import economicOperatorApi from "@services/economicOperator";
import { Guid } from "@util/guid";
import { naturalSort } from "@util/naturalSort";
import _ from "lodash";
import { sha1 as hash } from "object-hash";

const defaultState = (): GpsrState => ({
  runningPromises: [],
  saveMessageSeen: true,
  errors: {},
  apiErrors: {},
  initialDataHash: {
    economicOperator: null
  },
  deleted: {
    economicOperator: []
  },
  isSaving: false,
  economicOperator: {
    economicOperators: [],
    attributes: []
  }
});

const getters: GpsrGetters = {
  hasUnsavedData: function (state: GpsrState) {
    return (
      !_.isEmpty(state.initialDataHash.economicOperator) && !_.isEqual(hash(state.economicOperator.economicOperators), state.initialDataHash.economicOperator)
    );
  },
  isBusy: function (state: GpsrState) {
    return !!state.runningPromises.length;
  },
  lastRunSuccess: function (state: GpsrState) {
    const result = Object.values(state.errors).every((x) => !x);
    return result;
  },
  sectionHasError: function (state: GpsrState) {
    return (section: string) => {
      return state.errors[section];
    };
  },
  isSectionModified: (state: GpsrState) => (section: keyof InitialDataHash) => {
    return state.initialDataHash[section] && state[section] && hash(state[section]) !== state.initialDataHash[section];
  },
  isSaving: function (state: GpsrState) {
    return state.isSaving;
  },
  fieldHasAPIError: function (state: GpsrState) {
    return (section: string, field: string) => {
      return state.apiErrors[section] && !!state.apiErrors[section][field];
    };
  }
};
const mutations: GpsrMutations = {
  "reset-state"(state) {
    Object.assign(state, defaultState());
  },
  "set-initial-hash": function (state: GpsrState, args: { section: keyof InitialDataHash; data: EconomicOperator[] }) {
    state.initialDataHash[args.section] = hash(args.data);
  },

  "notification-seen": function (state: GpsrState) {
    state.saveMessageSeen = true;
  },
  "add-running-promise": function (state: GpsrState, promise: Promise<any>) {
    state.runningPromises.push(promise);
  },
  "reset-running-promises": function (state: GpsrState) {
    state.runningPromises = [];
  },

  "mark-as-deleted": function (state: GpsrState, args: { section: string; value: Guid | number }) {
    if (Array.isArray(args.value)) {
      const items = state.deleted[args.section];
      items.push(...args.value);
    } else {
      state.deleted[args.section].push(args.value);
    }
  },
  "reset-deleted-section": function (state: GpsrState, section: string) {
    state.deleted[section] = [];
  },
  "set-section-error": function (state: GpsrState, args: { section: string; error: any }) {
    const errors = Object.assign({}, state.errors);
    errors[args.section] = args.error;
    state.errors = errors;
  },

  "notification-show": function (state: GpsrState) {
    state.saveMessageSeen = false;
  },
  "reset-errors": function (state: GpsrState) {
    const keys: (keyof ConfigurationManagerErrors)[] = Object.keys(state.errors) as (keyof ConfigurationManagerErrors)[];
    keys.forEach((key) => (state.errors[key] = null));
  },

  "set-is-saving": function (state: GpsrState, args: any) {
    state.isSaving = args;
  },

  "set-economic-operators": function (state: GpsrState, args: EconomicOperator[]) {
    state.economicOperator.economicOperators = args;
  },
  "set-economic-operator-attributes": function (state: GpsrState, args: EconomicOperatorAttribute[]) {
    state.economicOperator.attributes = args;
  }
};
const actions: GpsrActions = {
  resetRunningPromisesIfResolved: async function ({ state, commit }) {
    await Promise.all(state.runningPromises);
    commit("reset-running-promises");
  },
  loadAllSettings: async function ({ dispatch }) {
    dispatch("loadEconomicOperator");

    dispatch("resetRunningPromisesIfResolved");
  },
  saveAllSettings: async function ({ dispatch, commit, getters }) {
    try {
      if (getters.hasUnsavedData) {
        commit("set-is-saving", true);
        commit("reset-errors");
        commit("notification-show");

        if (getters.isSectionModified("economicOperator")) {
          await dispatch("saveEconomicOperator");
        }
        await dispatch("resetRunningPromisesIfResolved");
      }
    } catch (error) {
      eventLogger.error("Save failed", error);
    } finally {
      commit("set-is-saving", false);
    }
  },

  setInitialHash: function ({ commit }, args: { data: any; section: string }) {
    commit("set-initial-hash", { data: args.data, section: args.section });
  },

  markAsDeleted: function ({ commit }, args?: any) {
    commit("mark-as-deleted", args);
  },
  setSectionError: function ({ commit }, args?: any) {
    commit("set-section-error", args);
  },

  loadEconomicOperator: async function ({ state, commit, dispatch }) {
    let economicOperators = await economicOperatorApi.getEconomicOperators();
    economicOperators = naturalSort(economicOperators, (eo) => {
      const name = eo.attributeValues.find((av) => av.economicOperatorAttributeId === EconomicOperatorDefaultAttributes.ManufacturerName)?.value;
      return name || "";
    });
    commit("set-economic-operators", economicOperators);
    const economicOperatorAttributes = await economicOperatorApi.getEconomicOperatorAttributes();
    commit("set-economic-operator-attributes", economicOperatorAttributes);

    commit("set-initial-hash", { data: state.economicOperator.economicOperators, section: "economicOperator" });
    await dispatch("resetRunningPromisesIfResolved");
  },
  saveEconomicOperator: async function ({ state, commit }) {
    try {
      const economicOperatorsToSave = state.economicOperator.economicOperators;
      for (const economicOperator of economicOperatorsToSave) {
        await economicOperatorApi.save(economicOperator);
      }

      for (const economicOperatorId of state.deleted.economicOperator) {
        await economicOperatorApi.delete(economicOperatorId);
      }

      commit("set-initial-hash", { data: state.economicOperator.economicOperators, section: "economicOperator" });
      commit("reset-deleted-section", "economicOperator");
    } catch (error) {
      commit("set-section-error", {
        section: "economicOperator",
        error: error
      });
    }
  },
  setEconomicOperators: function ({ commit }, args: EconomicOperator[]) {
    commit("set-economic-operators", args);
  }
};

const storeModule = {
  namespaced: true,
  state: { ...defaultState() },
  getters,
  mutations,
  actions
};
export default storeModule;
